import { Text, View } from '@zinc/ui';
import FileUploadDropzone from 'bz-block-template/components/file-upload/FileUploadDropzone';
import PrintedFileList, {
  FormFilePrintModeContext
} from 'bz-block-template/components/file-upload/PrintedFileList';
import SubmittedFileList from 'bz-block-template/components/file-upload/SubmittedFileList';
import {
  DEFAULT_FILE_ACCEPT,
  DEFAULT_FILE_MAX_SIZE_BYTES,
  type FileUploadData,
  useFileUploadState
} from 'bz-block-template/components/file-upload/useFileUploadState';
import type FormStore from 'bz-draft/modules/stores/form-store';
import useStore from 'bz-stores/hook/mobx-inject/useStore';
import Label from 'click-controls/controls/form/field/common/label';
import { observer } from 'mobx-react';
import React from 'react';
import { v4 as uuid } from 'uuid';
export type FileUploadFieldData = FileUploadData;

export interface IProps {
  value?: FileUploadFieldData | null;
  onChange?: (value: FileUploadFieldData) => void;
  readOnly?: boolean;
  disabled?: boolean;
  formId?: string | null;
  label?: string;
}

const DraftFileUpload: React.FC<IProps> = observer((props: IProps) => {
  const { readOnly, disabled, value, onChange, formId, label = 'Upload Documents' } = props;
  const isDisabled = !!disabled;
  const draftStore = useStore<FormStore>('draftStore');
  const fieldIdRef = React.useRef(uuid());
  const fieldId = fieldIdRef.current;
  const isPrint = React.useContext(FormFilePrintModeContext);

  React.useEffect(() => {
    return () => draftStore.clearFileEntries(fieldId);
  }, [draftStore, fieldId]);

  const { fileList, processFiles, onDropRejected, onRemove } = useFileUploadState({
    isDisabled,
    data: value ?? null,
    onUpdate: (updated) => onChange?.(updated),
    fileEntries: draftStore.getFileEntries(fieldId),
    addFileEntry: (entry) => draftStore.addFileEntry(fieldId, entry),
    updateFileEntry: (key, patch) => draftStore.updateFileEntry(fieldId, key, patch),
    removeFileEntry: (key) => draftStore.removeFileEntry(fieldId, key)
  });

  if (readOnly && isPrint) {
    return (
      <View>
        <Label label={label} />
        <PrintedFileList names={value?.uploadedFileNames ?? []} />
      </View>
    );
  }

  if (readOnly && !!formId) {
    return (
      <View>
        <Label label={label} />
        <SubmittedFileList formId={formId} tempFolderId={value?.tempFolderId ?? null} />
      </View>
    );
  }

  return (
    <View>
      <Label label={label} />
      <View>
        <Text variant="body-3" color="neutral">
          Accepted Types:{' '}
          <Text variant="body-3" color="neutral-faded" as="span">
            {DEFAULT_FILE_ACCEPT}
          </Text>
        </Text>
        <Text variant="body-3" color="neutral">
          Max size:{' '}
          <Text variant="body-3" color="neutral-faded" as="span">
            {Math.round(DEFAULT_FILE_MAX_SIZE_BYTES / (1024 * 1024))} MB
          </Text>
        </Text>
      </View>
      <FileUploadDropzone
        acceptedTypes={DEFAULT_FILE_ACCEPT}
        maxSizeBytes={DEFAULT_FILE_MAX_SIZE_BYTES}
        isDisabled={isDisabled}
        fileList={fileList}
        onDrop={processFiles}
        onDropRejected={onDropRejected}
        onRemove={onRemove}
        testIdPrefix="draft-file-upload"
      />
    </View>
  );
});

DraftFileUpload.displayName = 'DraftFileUpload';

export default DraftFileUpload;
